import React, { useEffect, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { Table } from "antd";
import axios from "axios";
import moment from "moment";
import { toast } from "react-toastify";
import { switchEndpoint } from "../../../../../redux/actions/endpoints";
import { notificationBus } from "../../../../../notificationBus";
import SubmitModal from "./submitModal";
import EditModal from "./editModal";
import ViewModal from "./viewModal";
import { getRequestChip, formatAmount } from "./helpers";
import { storage } from "../../../../../scopedStorage";

const BASE = `${switchEndpoint}/aggregation-center/center-float-requests`;
const OPEN_STATUSES = "pending,approved,processing";

const tierTag = (chip) => {
  if (!chip) return "—";
  return (
    <span
      style={{
        background: chip.bg,
        color: chip.color,
        borderRadius: 999,
        padding: "3px 10px",
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {chip.spinner && (
        <span
          className="spinner-border spinner-border-sm"
          style={{ width: 8, height: 8, marginRight: 5 }}
        />
      )}
      {chip.label}
    </span>
  );
};

const FloatRequestsComponent = ({ setTitleName }) => {
  const [openRequest, setOpenRequest] = useState(null);
  const [loadingOpen, setLoadingOpen] = useState(true);

  const [history, setHistory] = useState([]);
  const [historyMeta, setHistoryMeta] = useState({ current_page: 1, per_page: 15, total: 0 });
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const [submitModalShow, setSubmitModalShow] = useState(false);
  const [editRequest, setEditRequest] = useState(null);
  const [viewRequest, setViewRequest] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    setTitleName && setTitleName("Float Requests");
  }, [setTitleName]);

  const token = storage.getItem("token");
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchOpenRequest = () => {
    setLoadingOpen(true);
    axios
      .get(BASE, { headers: authHeaders, params: { status: OPEN_STATUSES, per_page: 1 } })
      .then(({ data }) => setOpenRequest(data.data?.[0] || null))
      .catch(() => {})
      .finally(() => setLoadingOpen(false));
  };

  const fetchHistory = (page = 1) => {
    setLoadingHistory(true);
    const params = { page, per_page: historyMeta.per_page };
    if (statusFilter) params.status = statusFilter;

    axios
      .get(BASE, { headers: authHeaders, params })
      .then(({ data }) => {
        setHistory(data.data || []);
        setHistoryMeta(data.meta || historyMeta);
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || "Failed to load float requests", {
          position: "top-right",
          autoClose: 4000,
          theme: "light",
        });
      })
      .finally(() => setLoadingHistory(false));
  };

  const refetchAll = () => {
    fetchOpenRequest();
    fetchHistory(historyMeta.current_page || 1);
  };

  useEffect(() => {
    refetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchHistory(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // The open request's own record is what the "View" modal is showing live —
  // keep it pointed at the freshest copy whenever the open request refreshes.
  useEffect(() => {
    if (openRequest && viewRequest && viewRequest.id === openRequest.id) {
      setViewRequest(openRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest]);

  // Refetch on float-request alerts — the requester gets notified when HOP
  // approves/rejects and when an admin starts processing, so this is the
  // signal to re-check rather than polling on a timer. The center channel
  // carries other alert categories too, so ignore anything that isn't ours.
  useEffect(() => {
    const unsubscribe = notificationBus.subscribe((payload) => {
      if (payload?.category !== "center_float_request") return;
      refetchAll();
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While the payout is actually moving (wallet_transfer), the doc calls out
  // refetch-on-focus as the alternative to polling — the balance only
  // updates once status flips to funded, so re-check whenever the tab
  // regains focus during that window.
  useEffect(() => {
    if (openRequest?.awaiting !== "wallet_transfer") return;
    const onFocus = () => fetchOpenRequest();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest?.awaiting]);

  const handleSubmitted = () => {
    setSubmitModalShow(false);
    refetchAll();
  };

  const handleExistingRequest = (message) => {
    setSubmitModalShow(false);
    toast.error(message, { position: "top-right", autoClose: 6000, theme: "light" });
    fetchOpenRequest();
  };

  const handleAmended = () => {
    setEditRequest(null);
    setViewRequest(null);
    refetchAll();
  };

  const handleCancel = (request) => {
    setCancelling(true);
    axios
      .delete(`${BASE}/${request.id}`, { headers: authHeaders })
      .then(() => {
        toast.success("Float request cancelled", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
        });
        setViewRequest(null);
        refetchAll();
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || "The request could not be cancelled.", {
          position: "top-right",
          autoClose: 4000,
          theme: "light",
        });
      })
      .finally(() => setCancelling(false));
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "created_at",
      render: (value) => moment(value).format("DD MMM YYYY"),
    },
    { title: "Reference", dataIndex: "reference_no" },
    { title: "Amount", dataIndex: "amount", render: formatAmount },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, record) => tierTag(getRequestChip(record)),
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_, record) => (
        <Button variant="outline-secondary" size="sm" onClick={() => setViewRequest(record)}>
          View
        </Button>
      ),
    },
  ];

  const openChip = getRequestChip(openRequest);

  return (
    <main>
      <div style={{ padding: 24 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <div>
            <h5 style={{ margin: 0, fontWeight: 600 }}>Float Requests</h5>
            <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
              Ask HOP to top up your wallet float
            </p>
          </div>
        </div>

        {loadingOpen ? (
          <Spinner animation="border" size="sm" />
        ) : (
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: "16px 20px",
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            {openRequest ? (
              <>
                <div>
                  <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>
                    {openRequest.reference_no}
                  </p>
                  <p style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>
                    {formatAmount(openRequest.amount)}
                  </p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ marginBottom: 8 }}>{tierTag(openChip)}</div>
                  <Button
                    variant="outline-dark"
                    size="sm"
                    onClick={() => setViewRequest(openRequest)}
                  >
                    View Request
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
                  No float request in progress.
                </p>
                <Button variant="dark" size="sm" onClick={() => setSubmitModalShow(true)}>
                  Request Float
                </Button>
              </>
            )}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: "10px 14px",
            marginBottom: 14,
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600 }}>History</span>

          <Form.Control
            as="select"
            size="sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ width: 160 }}
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="processing">Processing</option>
            <option value="funded">Funded</option>
            <option value="rejected">Rejected</option>
            <option value="failed">Failed</option>
          </Form.Control>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={history}
          loading={loadingHistory}
          scroll={{ x: 700 }}
          sticky={{ offsetHeader: 0 }}
          pagination={{
            current: historyMeta.current_page,
            pageSize: historyMeta.per_page,
            total: historyMeta.total,
            onChange: (page) => fetchHistory(page),
          }}
        />

        <SubmitModal
          show={submitModalShow}
          onHide={() => setSubmitModalShow(false)}
          onSubmitted={handleSubmitted}
          onExistingRequest={handleExistingRequest}
        />

        <EditModal
          show={!!editRequest}
          onHide={() => setEditRequest(null)}
          request={editRequest}
          onAmended={handleAmended}
        />

        <ViewModal
          show={!!viewRequest}
          onHide={() => setViewRequest(null)}
          request={viewRequest}
          onEdit={(request) => {
            setViewRequest(null);
            setEditRequest(request);
          }}
          onCancel={handleCancel}
          cancelling={cancelling}
        />
      </div>
    </main>
  );
};

export default FloatRequestsComponent;
